import type { ReactNode } from 'react';

/**
 * How much precision a student needs, stated once for every block that has a tolerance.
 *
 * THE RULE THIS ENCODES: disclose precision, never the answer. "±0.5" says how many digits to carry
 * and reveals nothing about the value; a RANGE ("4.5 to 5.5") is the answer itself. That is why the
 * option behind this is offered only on `exact` conditions, and why the field it lives on carries a
 * comment saying so — see `showAcceptedRange` in numerical-block.tsx.
 *
 * The caller owns "which tolerance am I describing" (is the option on, is the condition type
 * eligible, do several answers agree). This owns the decision table below, which is the part that
 * must not differ between blocks.
 */

/**
 * How a tolerance hint is set, everywhere one appears.
 *
 * Italic because it is GUIDANCE, and every block that shows one sits it near something that is not:
 * GPS puts it a few lines above a live accuracy reading in the same size and colour, and without the
 * slant the two read as one pair of grey lines saying opposite kinds of thing — what the teacher
 * requires versus what the device managed. The italic is the part that survives being read at a
 * glance, so it carries the distinction rather than position alone.
 *
 * Alignment is NOT here: GPS centres its hint under a centred instrument, Numerical left-aligns it
 * under a left-aligned field. That is layout, and it belongs to the call site.
 */
export const TOLERANCE_HINT_CLASS = 'text-xs italic text-ws-muted';

/** One answer's tolerance, in the shape Numerical, Slider, Compass and Angle all store it. */
export interface ToleranceSpec {
  symmetry: 'symmetric' | 'asymmetric';
  /** Compass and Angle hardcode 'absolute': a percentage of a bearing is meaningless on a loop,
   *  since "360 ± 2%" is ±7.2° purely from the choice of numeral. Defaults accordingly. */
  toleranceMode?: 'absolute' | 'percentage';
  toleranceValue: string;
  toleranceAbove: string;
  toleranceBelow: string;
}

/**
 * The sign glyphs, held upright inside an italic line.
 *
 * Geist ships `styles: ['normal']` and no italic @font-face, so every italic in this app is
 * SYNTHETIC oblique — the browser shears the upright glyph. Letterforms survive that because they
 * have vertical stems to anchor on; `±` and `-` are defined by a thin horizontal bar, which the
 * shear pushes off the pixel grid, and at 12px in a muted colour there is no contrast left for
 * antialiasing to recover it. The result reads as blurry.
 *
 * Upright symbols inside italic text is also just conventional typography, so this costs nothing
 * beyond the node return. The NUMBERS stay italic: they are the guidance.
 */
const sign = (glyph: string) => <span className='not-italic'>{glyph}</span>;

export function toleranceHint(
  spec: ToleranceSpec,
  opts: {
    fmt: (n: number) => string;
    t: (k: string, o?: Record<string, unknown>) => string;
    /** Appended to each number: '°' for a bearing, '' where the unit already sits beside the input.
     *  Ignored under `percentage`, where the '%' is the unit. */
    unit?: string;
  },
): ReactNode | null {
  const { fmt, t, unit = '' } = opts;
  const exact = () => t('blocks.tolerance.exact');
  /**
   * Interpolate a NODE into the translated label, without splitting the label into two strings.
   *
   * `t` returns a string, so the usual trick of concatenating "Tolerance: " with a node in code is
   * not available here — and doing it anyway would hand translators a dangling fragment and fix the
   * word order to English. Instead the label is asked for once with a sentinel in the slot, then cut
   * at the sentinel: whatever a translator put on either side is preserved, including a language
   * that puts the value first.
   */
  const labelled = (value: ReactNode) => {
    const SLOT = '\u0000';
    const [before, after = ''] = t('blocks.tolerance.hint', { value: SLOT }).split(SLOT);
    return <>{before}{value}{after}</>;
  };
  const suffix = spec.toleranceMode === 'percentage' ? '%' : unit;

  // A BLANK tolerance reads exactly like an explicit 0, because that is how it GRADES: every path
  // into an evaluator does `parseFloat(field || '0')`, so an unfilled field is a zero-width band.
  // The student is told what the block will actually do, and whether the teacher chose that or
  // defaulted into it is not a distinction they can act on. (It IS one the ISSUE system cares
  // about — a blank field is never nagged about — so the two surfaces differ here on purpose.)
  //
  // Zero tolerance is the strictest setting available: the precise value, no rounding slack. That
  // makes it the single most useful thing this can say, and the worst case to stay silent about.
  //
  // A NEGATIVE or malformed tolerance says nothing. Neither is reachable by typing, since none of
  // these fields passes `allowNegative` and the grammar strips the sign — but the check mirrors the
  // `numberError` validation beside them rather than leaving one of the pair unguarded.
  if (spec.symmetry === 'symmetric') {
    const err = parseFloat(spec.toleranceValue || '0');
    if (!Number.isFinite(err) || err < 0) return null;
    if (err === 0) return exact();
    return labelled(<>{sign('±')}{fmt(err)}{suffix}</>);
  }

  const above = parseFloat(spec.toleranceAbove || '0');
  const below = parseFloat(spec.toleranceBelow || '0');
  if (!Number.isFinite(above) || !Number.isFinite(below)) return null;
  if (above < 0 || below < 0) return null;
  if (above === 0 && below === 0) return exact();
  // Equal ends in asymmetric mode are a symmetric band written the long way, so they read as one.
  if (above === below) return labelled(<>{sign('±')}{fmt(above)}{suffix}</>);
  // Both ends are PRINTED, zeros included. "-0 to +4" would invite "negative zero to four of what?"
  // on its own, but the "Tolerance:" label answers that, and it plainly says no slack downward and
  // four units of it upward. Dropping a zero end was tried and rejected: "+4" alone says neither
  // what it is nor what it is relative to, and a one-sided band that hid its zero would read
  // exactly like a symmetric one.
  return labelled(<>{sign('-')}{fmt(below)}{suffix} to {sign('+')}{fmt(above)}{suffix}</>);
}
